export const columnLabelMap: Record<string, string> = {
  address_number: "Numéro d'adresse",
  address_extension: "Extension d'adresse",
  address_type: "Type d'adresse",
  address_name: 'Nom de la voie',
  address_place: 'Lieu-dit',
  address_stairs: 'Étage/Escalier/Appartement',
  address_comp: "Complément d'adresse",
  address_postalbox: 'Boîte postale',
  city: 'Ville',
  postal_code: 'Code postal',
  country: 'Pays',
  first_name: 'Prénom',
  last_name: 'Nom',
  email: 'Email',
  siret: 'SIRET',
  company_name: 'Raison sociale',
  document_id: 'ID du document',
  document_type: 'Type de document',
  gender: 'Genre',
  place_of_birth: 'Lieu de naissance',
  date_of_birth: 'Date de naissance',
  document_issue_date: 'Date de délivrance du document',
  vin: 'VIN',
  num_formule: 'Numéro de formule',
  registration: 'Immatriculation',
  phone: 'Téléphone',
  ext_url: 'Lien externe',
  details_link: 'Lien détails',
};

export const formatValidators: Record<string, (value: string) => boolean> = {
  email: (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
  siret: (value: string) => /^\d{14}$/.test(value.replace(/\s/g, '')),
  postal_code: (value: string) => /^\d{5}$/.test(value.replace(/\s/g, '')),
};

const formatLabels: Record<string, string> = {
  email: 'un email valide',
  siret: '14 chiffres',
  postal_code: '5 chiffres',
};

export interface ValidationField {
  key: string;
  value: unknown;
  required?: boolean;
  format?: 'email' | 'siret' | 'postal_code';
}

export interface ValidationResult {
  missing: string[];
  invalidFormat: string[];
}

export const validateFields = (fields: ValidationField[]): ValidationResult => {
  const missing: string[] = [];
  const invalidFormat: string[] = [];

  fields.forEach((field) => {
    const isRequired = field.required !== false;
    const label = columnLabelMap[field.key] || field.key;
    const isEmpty = field.value === null || field.value === undefined || String(field.value).trim() === '';

    if (isRequired && isEmpty) {
      missing.push(label);
      return;
    }

    if (field.format && !isEmpty) {
      const validator = formatValidators[field.format];
      if (validator && !validator(String(field.value))) {
        invalidFormat.push(`${label} (${formatLabels[field.format]})`);
      }
    }
  });

  return { missing, invalidFormat };
};

export const formatValidationError = (dealId: string | number, result: ValidationResult, context?: string): string => {
  const parts: string[] = [];
  const prefix = context ? `[${context}] ` : '';

  if (result.missing.length > 0) {
    parts.push(`${prefix}Deal ${dealId} - Champs manquants : ${result.missing.join(', ')}`);
  }

  if (result.invalidFormat.length > 0) {
    parts.push(`${prefix}Deal ${dealId} - Format invalide : ${result.invalidFormat.join(', ')}`);
  }

  return parts.join('\n');
};

export const formatBackendError = (error: any): string => {
  const rawMessage = error.response?.data?.error || error.response?.data?.message || error.message || '';

  const dataTooLongMatch = rawMessage.match(/Data too long for column '(\w+)'/);
  if (dataTooLongMatch) {
    const label = columnLabelMap[dataTooLongMatch[1]] || dataTooLongMatch[1];
    return `Le champ "${label}" contient trop de caractères. Veuillez raccourcir la valeur saisie.`;
  }

  const duplicateMatch = rawMessage.match(/Duplicate entry '(.+?)' for key '(\w+)'/);
  if (duplicateMatch) {
    const label = columnLabelMap[duplicateMatch[2]] || duplicateMatch[2];
    return `La valeur du champ "${label}" existe déjà. Veuillez saisir une valeur différente.`;
  }

  const cannotBeNullMatch = rawMessage.match(/Column '(\w+)' cannot be null/);
  if (cannotBeNullMatch) {
    const label = columnLabelMap[cannotBeNullMatch[1]] || cannotBeNullMatch[1];
    return `Le champ "${label}" est obligatoire.`;
  }

  const incorrectValueMatch = rawMessage.match(/Incorrect \w+ value.*for column '(\w+)'/);
  if (incorrectValueMatch) {
    const label = columnLabelMap[incorrectValueMatch[1]] || incorrectValueMatch[1];
    return `La valeur saisie pour le champ "${label}" est invalide. Veuillez vérifier le format.`;
  }

  return rawMessage || 'Erreur lors de la sauvegarde';
};
